/**
 * Storefront Gate - Blocks browsing, cart, and checkout until member verification
 */

import {
  clearMemberVerification,
  shouldClearMemberVerification,
  stripClearVerificationQueryParam,
} from './member-verification-utils.js';

class StoreGate {
  constructor() {
    this._config = this._parseConfig();
    this._isLocked = false;
    this._originalFetch = window.fetch;
    
    this._handleFormSubmit = this._handleFormSubmit.bind(this);
    this._handleClick = this._handleClick.bind(this);
    this._handleVerified = this._handleVerified.bind(this);
    this._handleCartUpdated = this._handleCartUpdated.bind(this);
  }

  async init() {
    if (shouldClearMemberVerification()) {
      await this._clearVerification();
      stripClearVerificationQueryParam();
    }

    this._detectOrderCompletion();

    if (this._isVerified()) {
      this._ensureCartAttributes();
      return;
    }

    this._lock();
    this._interceptEvents();
    this._patchFetch();
    this._listenForVerification();
    this._listenForCartUpdates();
    this._openModal();
  }

  _parseConfig() {
    const configEl = document.querySelector('[data-store-gate-config]');
    if (configEl) {
      try {
        return JSON.parse(configEl.textContent);
      } catch (e) {
        console.error('StoreGate: Failed to parse config', e);
      }
    }
    return {
      cookieName: 'sandals_verified',
      shopifyRoutesRoot: '/',
    };
  }

  _isVerified() {
    return this._getCookie(this._config.cookieName) === 'true';
  }

  _getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return decodeURIComponent(parts.pop().split(';').shift());
    }
    return null;
  }

  _lock() {
    this._isLocked = true;
    document.documentElement.classList.add('is-locked');
  }

  _unlock() {
    this._isLocked = false;
    document.documentElement.classList.remove('is-locked');
  }

  _interceptEvents() {
    document.addEventListener('submit', this._handleFormSubmit, true);
    document.addEventListener('click', this._handleClick, true);
  }

  _removeEventInterceptors() {
    document.removeEventListener('submit', this._handleFormSubmit, true);
    document.removeEventListener('click', this._handleClick, true);
  }

  _handleFormSubmit(event) {
    if (!this._isLocked) return;

    const form = event.target;
    if (!form || form.tagName !== 'FORM') return;

    const action = form.getAttribute('action') || '';
    if (action.includes('/cart/add') || action.includes('/cart') || action.includes('/checkout')) {
      event.preventDefault();
      event.stopPropagation();
      this._openModal();
    }
  }

  _handleClick(event) {
    if (!this._isLocked) return;

    const target = event.target;

    if (target.closest('[data-check-eligibility-trigger]')) {
      return;
    }

    const addToCartSelectors = [
      '[data-add-to-cart]',
      'button[name="add"]',
      '[type="submit"][name="add"]',
      '.product-form__submit',
      '.add-to-cart',
      '[data-ajax-cart-add-button]',
      '.launch-tee-add-button',
      '[data-launch-tee-submit]',
    ];

    const isAddToCart = addToCartSelectors.some(selector => target.closest(selector));
    
    if (isAddToCart) {
      event.preventDefault();
      event.stopPropagation();
      this._openModal();
      return;
    }

    const link = target.closest('a[href]');
    if (link) {
      const href = link.getAttribute('href') || '';
      const root = this._config.shopifyRoutesRoot || '/';
      
      const blockedPaths = [
        `${root}cart`,
        `${root}checkout`,
        '/cart',
        '/checkout',
      ];

      const isBlocked = blockedPaths.some(path => {
        return href === path || href.startsWith(path + '?') || href.startsWith(path + '/');
      });

      if (isBlocked) {
        event.preventDefault();
        event.stopPropagation();
        this._openModal();
        return;
      }
    }
  }

  _patchFetch() {
    const self = this;
    const originalFetch = this._originalFetch;

    window.fetch = function(input, init) {
      if (!self._isLocked) {
        return originalFetch.apply(this, arguments);
      }

      let url = '';
      if (typeof input === 'string') {
        url = input;
      } else if (input instanceof Request) {
        url = input.url;
      } else if (input instanceof URL) {
        url = input.toString();
      }

      const blockedPatterns = [
        '/cart/add',
        '/cart/add.js',
      ];

      const isBlocked = blockedPatterns.some(pattern => url.includes(pattern));

      if (isBlocked) {
        self._openModal();
        return Promise.reject(new Error('Store is locked. Please verify your membership first.'));
      }

      return originalFetch.apply(this, arguments);
    };
  }

  _restoreFetch() {
    window.fetch = this._originalFetch;
  }

  _openModal() {
    const open = () => {
      const modal = document.querySelector('check-eligibility-modal');
      if (modal && typeof modal.show === 'function') {
        modal.show(null, true);
      }
    };

    if (customElements.get('check-eligibility-modal')) {
      open();
      return;
    }

    customElements.whenDefined('check-eligibility-modal').then(open);
  }

  _listenForVerification() {
    document.addEventListener('sandals:verified', this._handleVerified);
  }

  _handleVerified(event) {
    this._unlock();
    this._removeEventInterceptors();
    this._restoreFetch();
    document.removeEventListener('sandals:verified', this._handleVerified);
  }

  _listenForCartUpdates() {
    document.addEventListener('liquid-ajax-cart:request-end', this._handleCartUpdated);
    
    if (window.liquidAjaxCart) {
      const originalUpdate = window.liquidAjaxCart.update;
      if (originalUpdate) {
        const self = this;
        window.liquidAjaxCart.update = function() {
          const result = originalUpdate.apply(this, arguments);
          setTimeout(() => self._ensureCartAttributes(), 100);
          return result;
        };
      }
    }
  }

  _handleCartUpdated() {
    this._ensureCartAttributes();
  }

  async _ensureCartAttributes() {
    if (!this._isVerified()) return;

    const ssg = this._getCookie('sandals_ssg');
    const tier = this._getCookie('sandals_member_tier');

    if (!ssg || !tier) return;

    try {
      const root = this._config.shopifyRoutesRoot || window.Shopify?.routes?.root || '/';
      const cartResponse = await this._originalFetch(`${root}cart.js`);
      const cart = await cartResponse.json();

      const currentSsg = cart.attributes?.SSG;
      const currentTier = cart.attributes?.['Member Tier'];

      if (currentSsg !== ssg || currentTier !== tier) {
        await this._originalFetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            attributes: {
              'SSG': ssg,
              'Member Tier': tier,
            },
          }),
        });
      }
    } catch (error) {
      console.error('StoreGate: Error ensuring cart attributes', error);
    }
  }

  _detectOrderCompletion() {
    const checkOrderCompletion = () => {
      const isThankYouPage =
        window.location.pathname.includes('/thank_you') ||
        window.location.pathname.includes('/orders/');

      if (isThankYouPage) {
        this._clearVerification();
      }
    };

    if (document.readyState === 'complete') {
      checkOrderCompletion();
    } else {
      window.addEventListener('load', checkOrderCompletion);
    }
  }

  async _clearVerification() {
    await clearMemberVerification(this._originalFetch);
  }
}

const storeGate = new StoreGate();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => storeGate.init());
} else {
  storeGate.init();
}

window.StoreGate = StoreGate;
